/* eslint-disable @typescript-eslint/no-explicit-any */
import { DatePicker } from "@octopusdeploy/design-system-components";
import { TextWithLabel as Text } from "@octopusdeploy/design-system-octopus-components";
import moment from "moment";
import * as React from "react";
import CopyValueToClipboard from "~/components/CopyToClipboardButton/CopyValueToClipboard";
import type { DataBaseComponentState } from "~/components/DataBaseComponent/DataBaseComponent";
import { DataBaseComponent } from "~/components/DataBaseComponent/DataBaseComponent";
import type { RenderProps } from "~/components/Dialog/CustomDialog";
import { CustomDialogActions, CustomDialogContent, CustomSaveDialogActions, SmallDialogFrame } from "~/components/DialogLayout/Custom";
import CustomSaveDialogLayout, { CustomSaveDialogTitleBar } from "~/components/DialogLayout/Custom/CustomSaveDialogLayout";
import type { Item } from "~/primitiveComponents/form/Select/Select";
import Select from "~/primitiveComponents/form/Select/Select";
interface ExpiryOption {
    isDefault: boolean;
    label: string;
    days: number; // negative value = "Custom".
}
interface AddApiKeyState extends DataBaseComponentState {
    purpose: string;
    expiryOptions: ExpiryOption[];
    selectedExpiryOption: ExpiryOption;
    customExpiryDate?: Date;
}
interface AddApiKeyProps extends RenderProps {
    apiKey?: string;
    apiKeyDefaultExpiryDays: number;
    apiKeyMaxExpiryDays: number;
    onAdd(purpose: string, expires: Date | null): Promise<any>;
    onClose(): void;
}
class AddApiKeyDialogLayout extends DataBaseComponent<AddApiKeyProps, AddApiKeyState> {
    constructor(props: AddApiKeyProps) {
        super(props);
        // generate the options based on policy
        const defaultExpiryDays = this.props.apiKeyDefaultExpiryDays;
        const dayOptions = [30, 60, 90, 180, 365, 730, 1095].filter((d) => d <= this.props.apiKeyMaxExpiryDays);
        // add the default to dayOptions if it's not already there.
        if (!dayOptions.includes(defaultExpiryDays)) {
            const insertionIndex = dayOptions.findIndex((o) => o > defaultExpiryDays);
            if (insertionIndex >= 0) {
                dayOptions.splice(insertionIndex, 0, defaultExpiryDays);
            }
            else {
                // it's the largest value, add to the end
                dayOptions.push(defaultExpiryDays);
            }
        }
        // map the numbers to the option objects with descriptions.
        const expiryOptions: ExpiryOption[] = dayOptions.map((d) => ({
            isDefault: d == defaultExpiryDays,
            label: this.labelFor(d, defaultExpiryDays),
            days: d,
        }));
        // find the default option and select it
        const selectedExpiryOption = expiryOptions.find((o) => o.days === defaultExpiryDays); // this should always be non-null because we just added it to the list above.
        if (!selectedExpiryOption)
            throw new Error("Could not find the default expiry option in the list of options.");
        // Custom always goes last
        expiryOptions.push({ isDefault: false, label: "Custom", days: -1 });
        this.state = {
            purpose: "",
            expiryOptions,
            selectedExpiryOption,
        };
    }
    componentDidMount() {
        this.clearErrors();
    }
    render() {
        return (<CustomSaveDialogLayout {...this.props} frame={SmallDialogFrame} close={this.props.close} open={this.props.open} renderTitle={() => <CustomSaveDialogTitleBar title="Generate New API Key"/>} busy={this.state.busy} errors={this.errors} onSaveClick={this.saveAndShowKey} renderActions={(renderProps) => (<CustomDialogActions actions={<CustomSaveDialogActions close={renderProps.close} saveButtonLabel={this.props.apiKey ? "Close" : "Generate New"} hideCancel={!!this.props.apiKey} onSaveClick={renderProps.onSaveClick} savePermission={renderProps.savePermission}/>}/>)} renderContent={(renderProps) => <CustomDialogContent>{this.props.apiKey ? this.renderResult() : this.renderSetupApiKey()}</CustomDialogContent>}/>);
    }
    private labelFor(days: number, defaultDays: number) {
        let baseText = `${days} days`;
        if (days == 365)
            baseText = "1 year";
        else if (days == 730)
            baseText = "2 years";
        else if (days == 1095)
            baseText = "3 years";
        return days === defaultDays ? `${baseText} (default)` : baseText;
    }
    private saveAndShowKey = async () => {
        if (this.props.apiKey) {
            this.props.onClose();
            return false;
        }
        if (this.isCustomExpiryOptionSelected() && !this.state.customExpiryDate) {
            this.setValidationErrors("There was a problem with your request.", { customDate: "Please select a date" });
            return false;
        }
        const expires = this.determineExpiry();
        await this.doBusyTask(() => this.props.onAdd(this.state.purpose, expires));
        return false;
    };
    private changeExpiryOffset = (newValue?: string) => {
        this.clearErrors();
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const expiryOptionDaysValue = newValue === undefined ? undefined : parseInt(newValue, 10);
        const selectedExpiryOption = this.state.expiryOptions.find((o) => o.days == expiryOptionDaysValue);
        if (!selectedExpiryOption) {
            // should never happen, the combo box limits the options
            throw new Error(`Could not find expiry option with days value of ${expiryOptionDaysValue}`);
        }
        this.setState({ selectedExpiryOption, customExpiryDate: undefined });
    };
    private selectDate = (customExpiryDate?: Date) => {
        this.clearErrors();
        this.setState({ customExpiryDate });
    };
    private isCustomExpiryOptionSelected() {
        return this.state.selectedExpiryOption.days < 0;
    }
    private determineExpiry(): Date {
        if (this.isCustomExpiryOptionSelected()) {
            return moment(this.state.customExpiryDate).endOf("day").toDate();
        }
        return moment().endOf("day").add(this.state.selectedExpiryOption.days, "days").toDate();
    }
    private renderResult() {
        return (this.props.apiKey && (<div>
                    <p>Your new API key is:</p>
                    <CopyValueToClipboard value={this.props.apiKey}/>
                    <p>API keys cannot be retrieved once they are created. Make sure you save this key in a safe place like a password management tool.</p>
                </div>));
    }
    private renderSetupApiKey() {
        const expiryItems: Item[] = this.state.expiryOptions.map((o) => ({
            text: o.label,
            value: o.days.toString(),
        }));
        const showCustomExpiry = this.isCustomExpiryOptionSelected();
        return (<div>
                <p>
                    API keys can be used to authenticate with the Octopus Deploy REST API in place of a username and password. Using API keys lets you keep your username and password secret, but the API key itself is still sensitive information that
                    needs to be protected.
                </p>
                <Text value={this.state.purpose} onChange={(purpose) => this.setState({ purpose })} autoFocus={true} label="Purpose"/>
                <p>Record the purpose of this key so that you can revoke it when no longer required.</p>
                <h4>When should this API key expire?</h4>
                <Select items={expiryItems} value={this.state.selectedExpiryOption.days.toString()} onChange={this.changeExpiryOffset} label="Set expiry date" sortItems={false}/>
                {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                {showCustomExpiry && <DatePicker value={this.state.customExpiryDate!} onChange={this.selectDate} label="Set custom expiry date" minDate={new Date()} error={this.getFieldError("customDate")}/>}
            </div>);
    }
    static displayName = "AddApiKeyDialogLayout";
}
export default AddApiKeyDialogLayout;
